import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// Mirrors the Figma Design System v0.3.0 Button component:
// 4 variants (Primary / Secondary / Ghost / Destructive) × 3 sizes (Sm / Md / Lg).
// `default` is an alias for the Md size; `icon` is a code-only square variant.
const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "bg-[var(--bg-brand)] text-[var(--text-on-brand)] hover:bg-[var(--bg-brand-hover)] focus-visible:ring-[var(--border-focus)]",
                secondary:
                    "bg-[var(--bg-surface)] text-[var(--text-primary)] border border-[var(--border-default)] hover:bg-[var(--bg-subtle-hover)] hover:border-[var(--border-strong)] focus-visible:ring-[var(--border-strong)]",
                ghost:
                    "text-[var(--text-primary)] hover:bg-[var(--bg-subtle-hover)] focus-visible:ring-[var(--border-strong)]",
                destructive:
                    "bg-[var(--bg-danger)] text-[var(--text-on-brand)] hover:bg-[var(--bg-danger-hover)] focus-visible:ring-[var(--border-danger)]",
            },
            size: {
                default: "h-10 px-4 text-sm leading-5 [&_svg]:size-4",
                // sm (32px) and icon (40px) keep their visual size; the pseudo-element
                // pads the hit area to the 44px touch-target token (FacetedFilter idiom).
                sm: "h-8 px-3 text-sm leading-5 [&_svg]:size-4 relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
                lg: "h-12 px-5 text-base leading-6 [&_svg]:size-5",
                icon: "h-10 w-10 [&_svg]:size-4 relative before:absolute before:-inset-0.5 before:content-['']",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = "Button"

export { Button, buttonVariants }
